/**
 * 🌌 About — page de marque YUMIX (cockpit allumé)
 *
 * Yuri spec 2026-02: la signature argent/blanc du logo + les couleurs néon
 * (orange, rouge, violet, bleu, émeraude) constituent un langage visuel
 * unique. Cette page ancre la marque comme produit de référence.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, Brain, Flame, Heart, Compass, ArrowRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';

const NEON_PALETTE = [
  { name: 'Orange Mission',   hex: '#fb923c', glow: 'rgba(251,146,60,0.65)',  desc_fr: 'Macros, victoires quotidiennes', desc_en: 'Macros, daily wins' },
  { name: 'Rouge Racing',     hex: '#ef4444', glow: 'rgba(239,68,68,0.65)',   desc_fr: 'Triple Victoire, défi haute intensité', desc_en: 'Triple Win, high-intensity challenge' },
  { name: 'Violet Électrique', hex: '#a855f7', glow: 'rgba(168,85,247,0.65)', desc_fr: 'Pas, Analyses Avancées', desc_en: 'Steps, Advanced Analytics' },
  { name: 'Bleu Cyan',        hex: '#22d3ee', glow: 'rgba(34,211,238,0.65)',  desc_fr: 'Hydratation, Précision FatSecret', desc_en: 'Hydration, FatSecret Precision' },
  { name: 'Émeraude',         hex: '#34d399', glow: 'rgba(52,211,153,0.65)',  desc_fr: 'Fenêtre alimentaire, énergie', desc_en: 'Eating window, energy' },
  { name: 'Argent Cockpit',   hex: '#e2e8f0', glow: 'rgba(255,255,255,0.6)',  desc_fr: 'Marque YUMIX, indicateur power-on', desc_en: 'YUMIX brand, power-on indicator' },
];

const PILLARS = [
  { icon: Brain,       fr: { title: 'Mindset', desc: 'IA proactive qui pose la bonne question chaque matin et te résume tes 7 derniers jours.' },         en: { title: 'Mindset', desc: 'Proactive AI asks the right question every morning and recaps your last 7 days.' } },
  { icon: ShieldCheck, fr: { title: 'Précision', desc: 'Macros validées contre la base FatSecret Canada (1.5M+ aliments).' },                              en: { title: 'Precision', desc: 'Macros validated against FatSecret Canada (1.5M+ foods).' } },
  { icon: Flame,       fr: { title: 'Discipline', desc: 'Triple Victoire, micro-victoires, badges Citadelle. La science du petit plus.' },                  en: { title: 'Discipline', desc: 'Triple Win, micro-wins, Citadel badges. The science of the extra mile.' } },
  { icon: Heart,       fr: { title: 'Empathie', desc: 'Mode Blessure, Mode Clinique, Mode Junior. Chaque vie est respectée.' },                            en: { title: 'Empathy', desc: 'Injury Mode, Clinical Mode, Junior Mode. Every life is respected.' } },
];

export default function About() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto pb-20" data-testid="about-page">
        {/* Hero — giant pulsing logo */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div
            className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-6"
            style={{ animation: 'yumix-cockpit-pulse 3.6s ease-in-out infinite' }}
          >
            <img src="/icons/yumix_logo_sidebar.png" alt="YUMIX" className="w-24 h-24 object-contain" />
          </div>
          <h1
            className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-foreground mb-3"
            style={{ animation: 'yumix-text-pulse 3.6s ease-in-out infinite' }}
            data-testid="about-hero-title"
          >
            YUMIX
          </h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
            {fr
              ? 'Le centre de commande nutritionnel d\'Extra Miles. Conçu au Québec pour les athlètes du quotidien.'
              : 'The nutrition command center of Extra Miles. Designed in Quebec for everyday athletes.'}
          </p>
        </motion.div>

        {/* Manifesto */}
        <section className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-950/20 via-zinc-950/70 to-orange-950/15 p-6 mb-10"
                 style={{ boxShadow: '0 0 16px rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.05)' }}>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-foreground/60 mb-3">
            {fr ? 'Manifeste' : 'Manifesto'}
          </p>
          <h2 className="font-heading text-xl uppercase tracking-tight text-foreground mb-4">
            {fr ? 'La science du petit plus.' : 'The science of the extra mile.'}
          </h2>
          <p className="text-sm text-foreground/85 leading-relaxed">
            {fr
              ? "On ne croit pas aux régimes punitifs. On croit aux micro-victoires accumulées, aux questions qu'on se pose à 6h du matin, aux 200 grammes de protéines, à l'eau qu'on boit avant le café. YUMIX rend ces gestes lisibles, célébrés, mesurés. Pas pour te juger — pour te rendre fier."
              : "We don't believe in punitive diets. We believe in stacked micro-wins, the questions you ask at 6am, the 200 grams of protein, the water you drink before coffee. YUMIX makes those acts visible, celebrated, measured. Not to judge — to make you proud."}
          </p>
        </section>

        {/* 4 Piliers */}
        <section className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-foreground/60 mb-4">
            {fr ? 'Les 4 piliers' : 'The 4 pillars'}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              const text = fr ? p.fr : p.en;
              return (
                <div key={i} className="rounded-xl border border-border/60 bg-card/40 p-4 hover:bg-card/60 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-cyan-300" />
                    <h3 className="font-heading text-sm uppercase tracking-tight text-foreground">{text.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{text.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Langage visuel — palette néon */}
        <section className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-foreground/60 mb-1">
            {fr ? 'Langage visuel' : 'Visual language'}
          </p>
          <h2 className="font-heading text-xl uppercase tracking-tight text-foreground mb-4">
            {fr ? '6 couleurs, 1 cockpit' : '6 colors, 1 cockpit'}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NEON_PALETTE.map((c) => (
              <div
                key={c.name}
                className="rounded-xl bg-zinc-950/60 ring-1 ring-zinc-800 p-4"
                style={{ boxShadow: `0 0 12px ${c.glow}, inset 0 0 0 1px ${c.glow.replace('0.65', '0.15')}` }}
                data-testid={`palette-${c.hex.replace('#', '')}`}
              >
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span
                    className="w-5 h-5 rounded-full"
                    style={{ background: c.hex, boxShadow: `0 0 10px ${c.glow}` }}
                  />
                  <span className="font-heading text-sm uppercase tracking-tight text-foreground">{c.name}</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {fr ? c.desc_fr : c.desc_en}
                </p>
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground/60 font-mono mt-1 inline-block">
                  {c.hex}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Stats sociales */}
        <section className="rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-950/25 via-zinc-950/70 to-blue-950/20 p-5 mb-10"
                 style={{ boxShadow: '0 0 14px rgba(34,211,238,0.35)' }}>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-cyan-300 mb-3">
            {fr ? 'Données vérifiées' : 'Verified data'}
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="font-heading text-2xl font-black text-cyan-100 leading-none">1.5M+</p>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300/80 mt-1.5">
                {fr ? 'Aliments FatSecret' : 'FatSecret foods'}
              </p>
            </div>
            <div>
              <p className="font-heading text-2xl font-black text-cyan-100 leading-none">🇨🇦</p>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300/80 mt-1.5">
                {fr ? 'Marché Canada' : 'Canada market'}
              </p>
            </div>
            <div>
              <p className="font-heading text-2xl font-black text-cyan-100 leading-none">30j</p>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300/80 mt-1.5">
                {fr ? 'Mémoire IA' : 'AI memory'}
              </p>
            </div>
          </div>
        </section>

        {/* Coach */}
        <section className="rounded-2xl border border-orange-400/30 bg-gradient-to-br from-orange-950/30 via-zinc-950/70 to-red-950/20 p-5 mb-10 text-center"
                 style={{ boxShadow: '0 0 14px rgba(251,146,60,0.4)' }}>
          <Compass className="w-6 h-6 text-orange-300 mx-auto mb-2" style={{ filter: 'drop-shadow(0 0 8px rgba(251,146,60,0.7))' }} />
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-orange-300 mb-1.5">
            {fr ? 'Conçu par' : 'Designed by'}
          </p>
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground mb-2">
            Yuri Octeau
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto">
            {fr
              ? "Coach Yuri Octeau et son équipe d'Extra Miles ont injecté 20+ ans d'accompagnement terrain dans chaque détail de l'app."
              : "Coach Yuri Octeau and his Extra Miles team injected 20+ years of field coaching into every detail of the app."}
          </p>
        </section>

        {/* CTA back */}
        <div className="text-center">
          <Link to="/dashboard">
            <Button
              className="bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white font-bold uppercase tracking-widest"
              style={{ boxShadow: '0 0 14px rgba(251,146,60,0.55)' }}
              data-testid="about-back-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {fr ? 'Retour au cockpit' : 'Back to cockpit'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Footer crédit */}
        <p className="text-center text-[9px] uppercase tracking-[0.3em] text-muted-foreground/60 mt-10">
          © {new Date().getFullYear()} Extra Miles · YUMIX · Québec, Canada
        </p>
      </div>
    </Layout>
  );
}
